import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { RefreshCw } from "lucide-react";
import {
  formStateFor,
  RESEND_SENT,
} from "@/modules/identity/commands/authCopy";
import { formatLebanesePhoneFull } from "@/modules/identity/surfaces";
import { Banner } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import {
  AuthHeading,
  CodeField,
  FootLink,
  ResendRow,
  SubmitButton,
} from "~/components/forms";
import { useMe } from "~/auth/MeProvider";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * PhoneVerificationScreen (docs/ux/screens/PhoneVerificationScreen.md).
 *
 * Serves **both** audiences since H47: Consumers (H22) and Business Members,
 * told apart by `?role=member`. The role is not a secret and forging it buys
 * nothing: it only selects which of two challenge sets is checked, and a code
 * for the wrong one simply does not match.
 *
 * **Where it goes afterwards differs by role, and that is H47 rather than
 * routing taste.** A Consumer has no session yet, because the number is proved
 * before a first login, so they land on the log-in screen. A Member already
 * holds a session and it was **masked** until this moment; verifying unmasks
 * the one they are holding, so re-reading `/me` is what lets them through.
 */
export function PhoneVerificationScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { reload } = useMe();
  const verify = useForm();
  const resend = useForm();
  const [code, setCode] = useState("");

  const phone = params.get("phone") ?? "";
  const unverified = params.get("unverified") === "1";
  const role = params.get("role") === "member" ? "member" : "consumer";
  const isMember = role === "member";
  const path = isMember ? "/auth/staff" : "/auth";

  // No phone to verify means nothing to do here; start where they came from.
  if (!phone) return <Navigate to={isMember ? "/join" : "/signup"} replace />;

  async function onVerify(event: React.FormEvent) {
    event.preventDefault();
    const result = await verify.submit(
      () => post(`${path}/verify`, { phone, code }),
      formStateFor,
      { phone },
    );
    if (!result) return;

    if (isMember) {
      // The session they are already holding is now unmasked; nothing new is
      // issued, so re-reading `/me` is the whole of "let them in".
      await reload();
      void navigate("/business", { replace: true });
    } else {
      void navigate("/login?verified=1", { replace: true });
    }
  }

  async function onResend(event: React.FormEvent) {
    event.preventDefault();
    const sent = await resend.submit(
      () => post(`${path}/resend`, { phone }),
      formStateFor,
      { phone },
    );
    // Always the same answer, so this cannot be used to probe which numbers
    // exist. The banner says a code was sent either way.
    if (sent) resend.setState({ success: RESEND_SENT });
  }

  return (
    <AuthShell>
      <AuthHeading
        title="Check your phone"
        sub={
          unverified
            ? "Your account isn't verified yet. Enter the code we sent you."
            : "We sent a 6-digit code. It expires shortly."
        }
      />
      <p className="mb-6 rounded-lg border border-line bg-page px-3 py-2 text-center font-mono text-sm text-ink">
        {formatLebanesePhoneFull(phone)}
      </p>

      {verify.state.formError && <Banner>{verify.state.formError}</Banner>}
      {resend.state.success && (
        <Banner tone="success">{resend.state.success}</Banner>
      )}
      {resend.state.formError && <Banner>{resend.state.formError}</Banner>}

      <form onSubmit={onVerify}>
        <fieldset disabled={verify.pending} className="disabled:opacity-70">
          <CodeField
            value={code}
            onChange={setCode}
            error={verify.state.fieldErrors?.code}
          />
          <SubmitButton pending={verify.pending} pendingLabel="Verifying…">
            Verify
          </SubmitButton>
        </fieldset>
      </form>

      <ResendRow>
        <form onSubmit={onResend}>
          <SubmitButton
            pending={resend.pending}
            pendingLabel="Sending…"
            variant="secondary"
          >
            <RefreshCw className="size-4" aria-hidden />
            Send it again
          </SubmitButton>
        </form>
      </ResendRow>

      <FootLink
        lead="Wrong number?"
        to={isMember ? "/join" : "/signup"}
        className="mt-4"
      >
        Start again
      </FootLink>
    </AuthShell>
  );
}
